import { AnimationSystem, CollisionSystem, DraggableWhenSelectedSystem, HandlePlacementSystem, HandlePlacementValidation, HistoryApi, InvalidFramePlacementSystem, ModelLoadedEventSystem, PlannerCoreApi, SelectionEventSystem, ProductType, OutlineSelectedFrameSystem, MeasurementsWidgetSystem, HighlightAddedEntitySystem, UnavailableProductsEventSystem, ConflictOutlineSystem, ConflictSystem, LegsFeature, SuspensionRailSystem, TopPanelSystem, WireBasketHeightValidation, HistoryEventSystem, HangingFramePlacementValidation, LightAccessoriesSystem } from '@inter-ikea-storageone/range';
import { ComponentIds, AutomaticGroupBuilderSystem } from '@inter-ikea-core/core';
import { AllowedNumberOfChildrenOfTypeValidation, AlignmentConnectionCreationSystem, FunctionalMarginSystem, FunctionalMarginValidation, OutlineSystem, PlacementZoneValidation, StandardSelectionSystem, RequiredConnectorsSatisfiedValidation, HostConnectorRotationValidation } from '@inter-ikea-core/core';
import PlatsaFrontComponent from './components/PlatsaFrontComponent';
import { PlatsaCameraApi } from './PlatsaCameraApi';
import { PlatsaEntityApi } from './PlatsaEntityApi';
import { PlatsaProductApi } from './PlatsaProductApi';
import { PlatsaSceneApi } from './PlatsaSceneApi';
import { PlatsaConflictResolver } from './resolvers/platsaConflictResolver';
import { PlatsaSlidingDoorPlacementSystem } from './systems/PlatsaSlidingDoorPlacementSystem';
import { PlatsaUncoupledSlidingDoorSystem } from './systems/PlatsaUncoupledSlidingDoorSystem';
import { PlatsaDoorsValidation } from './validations/PlatsaDoorsValidation';
import { PlatsaPullOutInteriorValidation } from './validations/PlatsaPullOutInteriorValidation';
import { PlatsaStackedFrameValidation } from './validations/PlatsaStackedFrameValidation';
import { PlatsaSoftClosingHingeSystem } from './systems/PlatsaSoftClosingHingeSystem';
import { SimpleOutlineSelectedSystem } from '@inter-ikea-core/core-toolbox';
import { lightsInSceneNeedControler } from '../utils/lights';
export class PlatsaCoreApi extends PlannerCoreApi {
    bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService, placementService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new OutlineSystem());
        this.registerSystem(new AutomaticGroupBuilderSystem(this.core.services.entityManager));
        this.registerSystem(new SimpleOutlineSelectedSystem());
        this.registerSystem(new StandardSelectionSystem(this.core.services.entityManager, this.core.services.sceneManager));
        this.registerSystem(new FunctionalMarginSystem());
        this.registerSystem(new AlignmentConnectionCreationSystem(this.getServices()));
        // Register core validations
        this.registerValidation(new RequiredConnectorsSatisfiedValidation());
        this.registerValidation(new HostConnectorRotationValidation());
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        this.registerValidation(new AllowedNumberOfChildrenOfTypeValidation(PlatsaFrontComponent.id, ComponentIds.HANDLE_COMPONENT_ID, 1));
        this.registerValidation(new PlacementZoneValidation());
        const collisionValidation = this.core
            .getValidations()
            .find((validation) => validation.id === 'Collision');
        collisionValidation.enableExhaustiveValidation();
        // Setup plannner APIs
        this.history = new HistoryApi(this);
        this.products = new PlatsaProductApi(this.core.services, placementService, this.reportErrorToSentry);
        this.entities = new PlatsaEntityApi(selectionService, this.core, placementService, this.ipexViewer, this.reportErrorToSentry);
        this.conflictResolver = new PlatsaConflictResolver(this);
        // Register planner validations
        this.registerValidation(new PlatsaDoorsValidation(this));
        this.registerValidation(new PlatsaPullOutInteriorValidation(this));
        this.registerValidation(new PlatsaStackedFrameValidation(this));
        this.registerValidation(new HandlePlacementValidation(this));
        this.registerValidation(new WireBasketHeightValidation(this, ProductType.PLATSA_WIRE_BASKET_ASM));
        this.registerValidation(new HangingFramePlacementValidation(this));
        // Register planner systems
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new ConflictSystem(this));
        this.registerSystem(new ConflictOutlineSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new CollisionSystem(this, [ProductType.PLATSA_TOP]));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new AnimationSystem(this));
        this.registerSystem(new HandlePlacementSystem(this));
        this.registerSystem(new DraggableWhenSelectedSystem(this, [
            ProductType.PLATSA_HINGE_DOOR_ASM,
            ProductType.PLATSA_SC_HINGE_DOOR_ASM,
            ProductType.PLATSA_DRAWER_ASM
        ]));
        this.registerSystem(new TopPanelSystem(this, ProductType.PLATSA_TOP));
        this.registerSystem(new SuspensionRailSystem(this, [
            ProductType.PLATSA_OPEN_FRAME_SHELVES,
            ProductType.PLATSA_OPEN_FRAME_HANGING,
            ProductType.PLATSA_OPEN_FRAME_SHOES
        ], ProductType.PLATSA_SUSPENSION_RAIL, 3000));
        this.registerSystem(new OutlineSelectedFrameSystem(this));
        this.registerSystem(new MeasurementsWidgetSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new PlatsaSlidingDoorPlacementSystem(this));
        this.registerSystem(new PlatsaUncoupledSlidingDoorSystem(this));
        this.registerSystem(new PlatsaSoftClosingHingeSystem(this));
        this.registerSystem(new LightAccessoriesSystem(this, lightsInSceneNeedControler));
        new LegsFeature(this, this.products.getDefaultLeg());
        return this.loadProductCatalogs(config.productCatalogs);
    }
    initSceneApi() {
        this.scene = new PlatsaSceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
    }
    initCameraApi(canvas) {
        this.camera = new PlatsaCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
    }
    getMainProductCatalog() {
        return this.catalogs['platsa'];
    }
    /**
     *
     * @override
     */
    async loadScene(config, setRoom) {
        // We need to wait for the scene to load before loading the legs system
        // otherwise the frames will not get the correct components added.
        this.getSystem('LegsSystem')?.disable();
        this.validations['LegsValidation']?.disable();
        this.validations['HangingFramePlacementValidation']?.disable();
        await super.loadScene(config, setRoom);
        this.validations['LegsValidation']?.enable();
        // When the scene has been loaded we can enable the legs system again
        this.getSystem('LegsSystem')?.enable();
        this.validations['HangingFramePlacementValidation']?.enable();
    }
}
//# sourceMappingURL=PlatsaCoreApi.js.map